/**
 * Token Rendering Utilities
 *
 * Shared utilities for rendering @Room and #Device tokens in the AI Scene Chatbot.
 * Used by both RichMentionInput and ChatWindow for consistent token styling.
 */

import { escapeHtml, escapeRegExp } from './string-utils';

/**
 * Auto-tokenization suggestion - represents a detected room/device name
 * that can be converted to a token with Tab key
 */
export interface AutoSuggestion {
	text: string;
	type: 'room' | 'device' | 'group' | 'scene';
	startIndex: number;
	endIndex: number;
}

/**
 * Options for customizing token rendering
 */
export interface RenderTokenOptions {
	/**
	 * CSS class prefix for tokens
	 * - 'ai-mention-token' for editable inputs (RichMentionInput)
	 * - 'suggestion-token' for display-only contexts (ChatWindow suggestions)
	 *
	 * Note: We use 'ai-mention-token' instead of 'token' to avoid conflicts with
	 * PrismJS syntax highlighting in the Scripts Editor (.shelly-code-editor .token).
	 */
	classPrefix?: 'ai-mention-token' | 'suggestion-token';

	/**
	 * Whether to add contenteditable="false" and data-token attributes
	 * Used for editable contenteditable elements
	 */
	addEditableAttributes?: boolean;

	/** Group names to match with $ prefix */
	groupNames?: string[];

	/** Scene names to match with % prefix */
	sceneNames?: string[];

	/** Whether to hide the glyph prefix (@, #, $, %) in token display */
	hideGlyphs?: boolean;

	/** Whether to show Font Awesome icons inside tokens (replaces glyph when hideGlyphs is true) */
	showIcons?: boolean;
}

/**
 * Render text with styled @Room, #Device, $Group, and %Scene tokens
 *
 * @param text - The raw text containing @Room, #Device, $Group, %Scene references
 * @param roomNames - Array of valid room names to match
 * @param deviceNames - Array of valid device names to match
 * @param options - Rendering options
 * @returns HTML string with tokens wrapped in styled spans
 */
export function renderTokens(
	text: string,
	roomNames: string[],
	deviceNames: string[],
	options: RenderTokenOptions = {},
): string {
	if (!text) return '';

	const {
		classPrefix = 'ai-mention-token',
		addEditableAttributes = false,
		groupNames = [],
		sceneNames = [],
		hideGlyphs = false,
		showIcons = false,
	} = options;

	let html = escapeHtml(text);

	// Sort by length (longest first) to avoid partial matches with multiword names
	const sortedRooms = [...roomNames].sort((a, b) => b.length - a.length);
	const sortedDevices = [...deviceNames].sort((a, b) => b.length - a.length);
	const sortedGroups = [...groupNames].sort((a, b) => b.length - a.length);
	const sortedScenes = [...sceneNames].sort((a, b) => b.length - a.length);

	// Build attributes string for editable contexts
	const editableAttrs = addEditableAttributes ? ' contenteditable="false"' : '';

	// Icon HTML for each token type (used when showIcons is true)
	const roomIcon = showIcons ? '<i class="fas fa-door-open"></i>' : '';
	const deviceIcon = showIcons ? '<i class="fas fa-plug"></i>' : '';
	const groupIcon = showIcons ? '<i class="fas fa-layer-group"></i>' : '';
	const sceneIcon = showIcons ? '<i class="fas fa-wand-magic-sparkles"></i>' : '';

	// Glyph prefixes (use HTML entities when showing, icons when showIcons, empty when hiding)
	const roomGlyph = showIcons ? roomIcon : hideGlyphs ? '' : '&#64;';
	const deviceGlyph = showIcons ? deviceIcon : hideGlyphs ? '' : '&#35;';
	const groupGlyph = showIcons ? groupIcon : hideGlyphs ? '' : '&#36;';
	const sceneGlyph = showIcons ? sceneIcon : hideGlyphs ? '' : '&#37;';

	// Replace @RoomName with styled spans
	for (const name of sortedRooms) {
		const pattern = new RegExp(`@${escapeRegExp(escapeHtml(name))}(?=\\s|$|[^\\w])`, 'g');
		const dataAttr = addEditableAttributes ? ' data-token="room"' : '';
		html = html.replace(
			pattern,
			`<span class="${classPrefix} room-token"${editableAttrs}${dataAttr}>${roomGlyph}${escapeHtml(name)}</span>`,
		);
	}

	// Replace #DeviceName with styled spans
	for (const name of sortedDevices) {
		const pattern = new RegExp(`#${escapeRegExp(escapeHtml(name))}(?=\\s|$|[^\\w])`, 'g');
		const dataAttr = addEditableAttributes ? ' data-token="device"' : '';
		html = html.replace(
			pattern,
			`<span class="${classPrefix} device-token"${editableAttrs}${dataAttr}>${deviceGlyph}${escapeHtml(name)}</span>`,
		);
	}

	// Replace $GroupName with styled spans
	for (const name of sortedGroups) {
		const pattern = new RegExp(`\\$${escapeRegExp(escapeHtml(name))}(?=\\s|$|[^\\w])`, 'g');
		const dataAttr = addEditableAttributes ? ' data-token="group"' : '';
		html = html.replace(
			pattern,
			`<span class="${classPrefix} group-token"${editableAttrs}${dataAttr}>${groupGlyph}${escapeHtml(name)}</span>`,
		);
	}

	// Replace %SceneName with styled spans
	for (const name of sortedScenes) {
		const pattern = new RegExp(`%${escapeRegExp(escapeHtml(name))}(?=\\s|$|[^\\w])`, 'g');
		const dataAttr = addEditableAttributes ? ' data-token="scene"' : '';
		html = html.replace(
			pattern,
			`<span class="${classPrefix} scene-token"${editableAttrs}${dataAttr}>${sceneGlyph}${escapeHtml(name)}</span>`,
		);
	}

	return html;
}

/**
 * Minimal option interface for auto-tokenization detection
 */
export interface TokenizableOption {
	name: string;
}

/**
 * Detect plain text that could be converted to a token
 *
 * Checks if the text before the cursor matches a room, device, group, or scene
 * name exactly, allowing the user to press Tab to convert it to a token.
 *
 * @param text - The full input text
 * @param cursorPos - Current cursor position
 * @param roomOptions - Array of room options with names
 * @param deviceOptions - Array of device options with names
 * @param groupOptions - Array of group options with names
 * @param sceneOptions - Array of scene options with names
 * @returns AutoSuggestion if a match is found, null otherwise
 */
export function detectAutoTokenizable(
	text: string,
	cursorPos: number,
	roomOptions: TokenizableOption[],
	deviceOptions: TokenizableOption[],
	groupOptions: TokenizableOption[] = [],
	sceneOptions: TokenizableOption[] = [],
): AutoSuggestion | null {
	const beforeCursor = text.slice(0, cursorPos);
	const words = beforeCursor.split(/\s+/);
	if (words.length === 0) return null;

	const tokenPrefixes = ['@', '#', '$', '%'];

	// Try matching 1, 2, 3 words from the end (for multi-word names like "Living Room")
	for (let numWords = Math.min(3, words.length); numWords >= 1; numWords--) {
		const candidateWords = words.slice(-numWords);
		const candidate = candidateWords.join(' ');

		if (tokenPrefixes.some((p) => candidate.startsWith(p))) continue;

		const startIndex = beforeCursor.length - candidate.length;

		const checks: Array<{ options: TokenizableOption[]; type: AutoSuggestion['type'] }> = [
			{ options: roomOptions, type: 'room' },
			{ options: deviceOptions, type: 'device' },
			{ options: groupOptions, type: 'group' },
			{ options: sceneOptions, type: 'scene' },
		];

		for (const { options, type } of checks) {
			const match = options.find((o) => o.name.toLowerCase() === candidate.toLowerCase());
			if (match) {
				return { text: match.name, type, startIndex, endIndex: cursorPos };
			}
		}
	}

	return null;
}
